import { useEffect, useState } from 'react';

interface VotingCountdownProps {
  votingStartsAt: string | null;
  votingEndsAt: string | null;
}

type Status = 'not-started' | 'active' | 'ended';

interface TimeParts {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

function diffToParts(diffMs: number): TimeParts {
  const total = Math.max(0, diffMs);
  const days = Math.floor(total / (1000 * 60 * 60 * 24));
  const hours = Math.floor((total % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((total % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((total % (1000 * 60)) / 1000);
  return { days, hours, minutes, seconds };
}

function pad(n: number): string {
  return n.toString().padStart(2, '0');
}

export function VotingCountdown({
  votingStartsAt,
  votingEndsAt,
}: VotingCountdownProps) {
  const startsAt = votingStartsAt ? new Date(votingStartsAt) : null;
  const endsAt = votingEndsAt ? new Date(votingEndsAt) : null;

  const [now, setNow] = useState<Date | null>(null);

  useEffect(() => {
    setNow(new Date());
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  if (!startsAt && !endsAt) return null;

  let status: Status = 'active';
  let target: Date | null = null;

  if (now) {
    if (startsAt && now < startsAt) {
      status = 'not-started';
      target = startsAt;
    } else if (endsAt && now < endsAt) {
      status = 'active';
      target = endsAt;
    } else {
      status = 'ended';
    }
  } else {
    // Pre-hydration skeleton: assume the most likely state to avoid a layout shift.
    status = startsAt && !endsAt ? 'not-started' : 'active';
    target = endsAt ?? startsAt;
  }

  if (status === 'ended') {
    return (
      <div className="mb-8 flex flex-col items-center gap-2 text-center">
        <span
          className="font-sans text-primary/60 text-xs uppercase sm:text-sm"
          style={{ letterSpacing: '0.3em' }}
        >
          Votación cerrada
        </span>
        <p className="font-bold font-sans text-3xl text-primary sm:text-4xl">
          ¡Gracias por participar!
        </p>
      </div>
    );
  }

  const parts =
    now && target ? diffToParts(target.getTime() - now.getTime()) : null;
  const label =
    status === 'not-started' ? 'La votación abre en' : 'La votación cierra en';

  const cells: Array<{ value: number | null; label: string }> = [
    { value: parts?.days ?? null, label: 'Días' },
    { value: parts?.hours ?? null, label: 'Horas' },
    { value: parts?.minutes ?? null, label: 'Min' },
    { value: parts?.seconds ?? null, label: 'Seg' },
  ];

  return (
    <div className="mb-8 flex flex-col items-center gap-4 text-center">
      <span
        className="font-sans text-primary/70 text-xs uppercase sm:text-sm"
        style={{ letterSpacing: '0.4em' }}
      >
        {label}
      </span>
      <div className="flex items-stretch gap-2 sm:gap-3 md:gap-4">
        {cells.map((cell, idx) => (
          <div
            key={cell.label}
            className="flex items-stretch gap-2 sm:gap-3 md:gap-4"
          >
            <div className="flex flex-col items-center">
              <div className="flex h-16 w-14 items-center justify-center border-2 border-primary/30 bg-primary/5 font-title text-3xl text-primary tabular-nums sm:h-20 sm:w-20 sm:text-5xl md:h-24 md:w-24 md:text-6xl">
                {cell.value === null ? '--' : pad(cell.value)}
              </div>
              <span
                className="mt-2 font-sans text-[10px] text-primary/60 uppercase sm:text-xs"
                style={{ letterSpacing: '0.2em' }}
              >
                {cell.label}
              </span>
            </div>
            {idx < cells.length - 1 && (
              <span className="self-center font-sans text-3xl text-primary/30 sm:text-5xl md:text-6xl">
                :
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
